import { Check, ChevronRight, User } from 'react-feather';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { Container, EventHeader, Footer } from '../Common/Layout';
import { encodeUrl } from '../../common/helpers';
import { useBrandColor } from '../../common/Common/ThemeProvider';
import { useNavigation } from '../Common/useNavigation';
import GetClaimedRegistrationQuery from './GetClaimedRegistrationQuery';
import UI from '../../common/UI';
import useDocumentTitle from '../../common/useDocumentTitle';
import useProject from '../useProject';
import useQuery from '../../api/useQuery';
import useUrlState from '../../common/useUrlState';

export interface ClaimSuccessPageProps {
  eventId: string;
}

const ClaimSuccessPage = ({
  eventId,
}: ClaimSuccessPageProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const project = useProject();

  const { data } = useQuery(
    GetClaimedRegistrationQuery,
    {
      variables: {
        event_id: eventId,
      },
      fetchPolicy: 'no-cache',
    },
  );

  const [{ email }] = useUrlState({
    parse: (params, { string }) => ({
      email: string(params.email),
    }),
  });

  const event = data?.event;

  useDocumentTitle([
    t('ticket_claimed'),
    event?.title,
    'Atleta',
  ]);

  useBrandColor(event?.brand_color);

  if (!data || !project) {
    return (
      <UI.PageLoader />
    );
  }

  return (
    <Container>
      <UI.Div sc={{ pt: [3, 4] }}>
        <EventHeader event={event} />
      </UI.Div>

      <UI.HR sc={{ mx: [0, 4] }} />

      <UI.GridContainer>
        <UI.Div sc={{ px: [2, 4] }}>
          <UI.H1 sc={{ fontSize: 4, color: 'secondary' }}>
            <UI.Icon sc={{ big: true, mr: 1 }}>
              <Check />
            </UI.Icon>
            {' '}
            {t('ticket_claimed_title')}
          </UI.H1>
        </UI.Div>
      </UI.GridContainer>

      <UI.GridContainer sc={{ gutter: 0.75 }}>
        <UI.Div sc={{ px: [2, 4] }}>
          {t('ticket_claimed_description')}
        </UI.Div>

        <NavContainer sc={{ hasPanels: true, mx: [2, 4] }}>
          <UI.LinkBlock
            href={`${r('Login')}${email ? `?${encodeUrl({ email })}` : ''}`}
            target="_blank"
            rel="noreferer noopener"
            title={t('frontend:success_page.go_to_dashboard')}
          >
            <UI.GridContainer sc={{ columns: '36px 1fr 16px', gutter: 0.75 }}>
              <UI.Div>
                <UI.Badge sc={{ size: 36, mt: 0.75 }}>
                  <UI.Icon sc={{ size: 1.125 }}>
                    <User />
                  </UI.Icon>
                </UI.Badge>
              </UI.Div>
              <UI.Div>
                <UI.Strong sc={{ block: true }}>
                  {t('frontend:success_page.go_to_dashboard')}
                </UI.Strong>
                {t('frontend:success_page.go_to_dashboard_description')}
              </UI.Div>
              <UI.Div>
                <NavIcon>
                  <ChevronRight />
                </NavIcon>
              </UI.Div>
            </UI.GridContainer>
          </UI.LinkBlock>
        </NavContainer>
      </UI.GridContainer>

      <Footer project={project} />
    </Container>
  );
};

const NavIcon = styled(UI.Icon)`
  ${({ theme }) => css`
  font-size: 1.2em;
    color: ${theme.colors.gray[400]};
    transition: all 0.1s ease-in-out;
  `}
`;

const NavContainer = styled(UI.Card)`
  ${({ theme }) => css`
    *:hover ${NavIcon} {
      margin-left: 3px;
      color: ${theme.colors.gray[800]};
    }
  `}
`;

export default ClaimSuccessPage;
